import merge from 'lodash/merge';

import cache from '../models/cache-model';

import {
    cacheEvents,
    cacheSelections,
    getDefaultEventId,
    getSportSlug,
    updateSelections,
} from '../../utils';

import {
    RACING_INIT,
    RACING_UPDATE_SCHEDULE,
    RACING_RACECARD_UPDATE,
    RACING_RACECARD_UPDATE_SELECTION_PRICE,
} from '../actions/types';

/**
 * Some thunk-reducer middleware
 * Looks for some key actions and grabbing data to cache
 * from them
 * @param {*} getState Function to get app state
 * @returns {*} Next middleware
 */
export default function cacheThunkMiddleware({ getState }) {
    return next => (actionContainer => {
        /**
         * Some actions created with redux batch
         * so they are arrays
         *
         * This that caction container should be iterated
         * so key actions might be found
         * RACING_INIT and RACING_UPDATE_SCHEDULE both returning a list
         * events, selections etc.
         */
        if (actionContainer instanceof Array) {
            for (const action of actionContainer) {
                if ([RACING_INIT, RACING_UPDATE_SCHEDULE].includes(action.type)) {
                    const sportSlug = getSportSlug(action.data.sports_details);
                    const cachedEvents = cacheEvents(action.data);
                    const cachedSelections = cacheSelections(getDefaultEventId(action.data), action.data);

                    cache.data = {
                        ...cache.data,
                        [sportSlug]: {
                            ...cache.data[sportSlug],
                            ...merge(cachedEvents, cachedSelections),
                        },
                    };
                }
            }
        } else {
            /**
             * Next two cases are about fetching racecards
             * and updating their data during certains ws events.
             * This time it's a price_update event action.
             */
            const sportSlug = getSportSlug(getState().sports_details);
            if (actionContainer.type === RACING_RACECARD_UPDATE) {
                const cachedSelections = cacheSelections(actionContainer.eventId, {
                    ...actionContainer.raceCardData,
                    featured: getState().featured,
                });

                cache.data = {
                    ...cache.data,
                    [sportSlug]: {
                        ...merge(cache.data[sportSlug], cachedSelections),
                    },
                };
            }

            /**
             * A price update branch.
             * It searches for a cached selections inside cache object
             * and updating them based on sport and race types.
             */
            if (actionContainer.type === RACING_RACECARD_UPDATE_SELECTION_PRICE) {
                const cachedSport = cache.getSportCache(sportSlug);
                if (cachedSport) {
                    actionContainer.data.forEach(event => {
                        const raceTypes = Object.keys(cachedSport);
                        raceTypes.forEach(raceType => {
                            const cachedData = cachedSport[raceType];
                            const cachedSelections = cachedData.selections[event.id];

                            if (cachedSelections) {
                                cachedData.selections[event.id] = updateSelections(event, cachedSelections);
                            }
                        });
                    });
                }
            }
        }

        next(actionContainer);
    });
}
